import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { IconArrowUp } from "../../../../common/icons/IconArrowUp/IconArrowUp";
import { useIsMobile } from "../../hooks/useIsMobile";
import "./CollapsibleSection.scss";

const TRANSITION_MS = 200;

const dispatchScrollDuringTransition = () => {
  const start = performance.now();
  const tick = () => {
    window.dispatchEvent(new Event("scroll"));
    if (performance.now() - start < TRANSITION_MS) {
      requestAnimationFrame(tick);
    }
  };
  requestAnimationFrame(tick);
};

interface Props {
  label: string;
  count: number;
  pinLevel?: number;
  children?: ReactNode;
  "data-testid"?: string;
  /** When true, disables the expand/collapse toggle on mobile entirely. */
  disableCollapse?: boolean;
  /** When true, enables the expand/collapse toggle on desktop and tablet as well as mobile. */
  enableDesktopCollapse?: boolean;
  /** Changing this value forces the section to expand (e.g. pass selectedLeader from the parent). */
  expandKey?: unknown;
  whosNewScrollToAbo?: { aff: string; abo: string; period: string };
  whosCloseScrollToAbo?: { aff: string; abo: string; bonusPercent: number };
  birthdayGroupScrollToAbo?: { aff: string; abo: string };
  scrollIdentifier?: string | number;
}

export const CollapsibleSection = ({
  label,
  count,
  pinLevel,
  children,
  "data-testid": dataTestId,
  disableCollapse,
  enableDesktopCollapse = false,
  expandKey,
  whosNewScrollToAbo,
  whosCloseScrollToAbo,
  birthdayGroupScrollToAbo,
  scrollIdentifier,
}: Props) => {
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isRendered, setIsRendered] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsExpanded(true);
    setIsRendered(true);
  }, [expandKey]);

  useEffect(() => {
    if (whosNewScrollToAbo && whosNewScrollToAbo.period === scrollIdentifier) {
      setIsRendered(true);
      setIsExpanded(true);
    }
    if (whosCloseScrollToAbo && whosCloseScrollToAbo?.bonusPercent === scrollIdentifier) {
      setIsRendered(true);
      setIsExpanded(true);
    }
    if (birthdayGroupScrollToAbo) {
      setIsRendered(true);
      setIsExpanded(true);
    }
  }, [whosNewScrollToAbo, whosCloseScrollToAbo, birthdayGroupScrollToAbo, scrollIdentifier]);

  if (count <= 0) {
    return null;
  }

  const handleToggle = () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (isExpanded) {
      setIsExpanded(false);
      dispatchScrollDuringTransition();
      timerRef.current = setTimeout(() => setIsRendered(false), TRANSITION_MS);
    } else {
      setIsRendered(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsExpanded(true);
          dispatchScrollDuringTransition();
        });
      });
    }
  };

  const headerContent = (
    <>
      <span className="CollapsibleSection__label">
        {label} ({count})
      </span>
      {pinLevel !== undefined && pinLevel > 0 && <span className="CollapsibleSection__pin">{pinLevel}</span>}
    </>
  );

  if ((!isMobile && !enableDesktopCollapse) || disableCollapse) {
    return (
      <section className="CollapsibleSection" data-testid={dataTestId}>
        <header className="CollapsibleSection__header">{headerContent}</header>
        <div className="CollapsibleSection__body">{children}</div>
      </section>
    );
  }

  return (
    <section className="CollapsibleSection" data-testid={dataTestId}>
      <button
        type="button"
        className="CollapsibleSection__header CollapsibleSection__header--button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
      >
        {headerContent}
        <span className={`CollapsibleSection__caret${isExpanded ? "" : " CollapsibleSection__caret--collapsed"}`}>
          <IconArrowUp backgroundClass="black" size="md" />
        </span>
      </button>
      <div
        className={`CollapsibleSection__body CollapsibleSection__body--collapsible${isExpanded ? " CollapsibleSection__body--expanded" : ""}`}
        aria-hidden={!isExpanded}
      >
        <div className="CollapsibleSection__bodyInner">{isRendered && children}</div>
      </div>
    </section>
  );
};
